import { useCallback, useContext, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { Button, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { isEqual } from "lodash";

import { ModalRolloverStatusDetails } from "../../cui/components/ModalRolloverStatusDetails/ModalRolloverStatusDetails";
import { useSnackbar } from "../../hooks";
import { SnackbarMessage } from "../../hooks/useSnackbar.types";
import useRolloverRequest from "../../hooks/useRolloverRequest";
import { refreshFlagAtom } from "../../constants";
import {
  createSitesClientApi,
  rollOverClientApi,
  taxPortalClientApi,
} from "../../api-client-nswag/taxportal-client-runtime";
import { GetUserRolloverDTO, ServiceBusEnum, ServiceMessage } from "../../api-client-nswag/taxportal-client";
import { RolloverRequestMessage } from "../../api-client-nswag/rollover-client";
import { SelectorType } from "../../cui/components/SelectorsHQ/SelectorsHQ.types";
import {
  NOTIFICATION_MESSAGES,
  ROLLOVER_OPTIONS,
  SERVICE_OPTIONS,
  ROLLOVER_SUBMIT_MODAL,
  getRolloverSubmitMessage,
  SITE_ROLLOVER_COMPLETION,
} from "./NewRollovers.constants";
import "./NewRollovers.scss";
import { SelectBox, TextBox } from "devextreme-react";
import { RolloverDateBox } from "./RolloverDateBox";
import { useSelectedDate } from "./DateContext";
import NewGridRollover from "../../cui/components/NewGridRollOver/NewGridRollover";
import NotificationPreferences, { NotificationSettings, DEFAULT_NOTIFICATION_SETTINGS } from "../../cui/components/NotificationPreferences";
import { DialogBox } from "../CreateSites/DialogBox";
import { formatDateForAPI, getDaysDifference } from "../../util/Utils";

const { SCHEDULE, INSER_TOPIC, CANCEL_SCHEDULE, ROLLOVER_NOW } = SERVICE_OPTIONS;
const {
  DEFAULT_SCHEDULE_DAYS,
} = NOTIFICATION_MESSAGES;

export default function NewRollovers() {
  const { user } = useContext(OidcIdentityContext);
  const refreshFlag = useRecoilValue(refreshFlagAtom);

  const [selectedRollover, setSelectedRollover] = useState<any | null>(null);
  const [newTaxYear, setNewTaxYear] = useState<any>("");
  const [isNewTaxYearValid, setNewTaxYearValid] = useState<boolean>(false);
  const { selectedDate, setSelectedDate } = useSelectedDate();

  var siteID = sessionStorage.getItem("siteId") as string;
  var {
    data,
    fetchData,
    clientID: mdmClientId,
    scheduleDays,
    rolloverOptions,
    taxYear,
    newTaxValue,
    appName,
  } = useRolloverRequest(parseInt(siteID));
  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false);
  const [isDisplayRolloverFilesModal, setIsDisplayRolloverFilesModal] =
    useState<boolean>(false);
  const [isDisplayStatusDetailsModal, setIsDisplayStatusDetailsModal] =
    useState<boolean>(false);
  const [isDisplayCompleteRolloverModal, setIsDisplayCompleteRolloverModal] = useState<boolean>(false);
  const [selectedFileId, setSelectedFileId] = useState<number>();
  const [selectedRolloverItem, setselectedRolloverItem] = useState<GetUserRolloverDTO>();
  const [selectedDetailsId, setSelectedDetailsId] = useState<number>();
  const [selection, setSelection] = useState<string>();
  const { showSnackbar } = useSnackbar();
    const rolloverDropdown = rolloverOptions || [];

  // Notification preferences state
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(DEFAULT_NOTIFICATION_SETTINGS);
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(undefined);
  const [isNotificationValid, setIsNotificationValid] = useState<boolean>(true);

  useEffect(() => {
    if (selection !== "FILES" && selectedDate) {
      setSelectedDate(null);
    }
  }, [selection]);

  useEffect(() => {
    // initialize scheduled date based on scheduleDays or default
    const days = scheduleDays ?? Number(DEFAULT_SCHEDULE_DAYS ?? 0);
    const d = new Date();
    d.setDate(d.getDate() + days);
    setScheduledDate(d);
  }, [scheduleDays]);

  useEffect(() => {
    const optSelected = getSelectedOptionsObj(undefined, selectedRollover).map(
      (opt) => opt.name,
    );
    optSelected.map((option) => {
      if (option === "FILES") {
        setSelection("FILES");
      } else {
        setSelection(option);
      }
    });
  }, [isDisplayModal, selectedRollover]);

  useEffect(() => {
    fetchData();
  }, [siteID, refreshFlag]);

  const rolloverOptionsDropdown = rolloverDropdown.map((el: any) => ({
    ...el,
    name: ROLLOVER_OPTIONS[el.name],
  }));

  const rolloverOptionsArr: any[] = Array.from(rolloverDropdown);
  const isValid = /^\d{4}$/.test(newTaxYear);

  const handleOpenModal = () => setIsDisplayModal(true);

  const handleCloseModal = () => setIsDisplayModal(false);

  const handleOpenRolloverFilesModal = (e: any) => {
    setselectedRolloverItem(e.row.data);
    setSelectedFileId(e.row.data.id);
    setIsDisplayStatusDetailsModal(false);
    setIsDisplayRolloverFilesModal(true);
  };

  const handleCloseRolloverFilesModal = () => {
    setSelectedFileId(0);
    setIsDisplayRolloverFilesModal(false);
  };

  const handleOpenStatusDetailsModal = (e: any) => {
    setSelectedDetailsId(e.row.data.id);
    setIsDisplayRolloverFilesModal(false);
    setIsDisplayStatusDetailsModal(true);
  };

  const handleCloseStatusDetailsModal = () => {
    setSelectedDetailsId(0);
    setIsDisplayStatusDetailsModal(false);
  };

  const getSelectedOptionsObj = (option?: string, id?: number) => {
    return rolloverOptionsArr.filter((item) =>
      option ? item.name === option : Number(item["id"]) == id,
    );
  };

  const requestRolloverNow = useCallback(
    (pendingRolloverRow: GetUserRolloverDTO) => {
        showSnackbar({ message: SnackbarMessage.ROLLOVER_NOW_PROGRESS });
        callService(ROLLOVER_NOW, { siteId: parseInt(siteID), requestRollover: pendingRolloverRow });
    }, []);

  const requestRollover = useCallback(
    (selected?: string, processTrackId?: number) => {
      handleCloseModal();
      let data: ServiceMessage[] = [];
      if (selected) setNewTaxYear(newTaxValue);
      const selectedObj = selected
        ? getSelectedOptionsObj(selected, undefined)
        : getSelectedOptionsObj(undefined, selectedRollover);

      const isFileData = selectedObj.some(
          (item) =>
            item.name === "FILES" ||
            item.name === "INFORMATION_REQUEST" ||
            item.name === "PROJECT_TRACKING" ||
            item.name === "DELIVERABLES" ||
            item.name === "K1TRACKER"
        );
   
      if (isFileData && !selected) {
        data = getDataToSend(selectedObj);
        showSnackbar({ message: SnackbarMessage.SCHEDULE_ROLLOVER });
        callService(SCHEDULE, {
          rollOverType: selectedRollover,
          siteId: parseInt(siteID),
          data: data,
          selectedDate: selectedDate,
          currentTaxYear: taxYear,
          newTaxYear: newTaxYear,
          appName: appName,
        });
        return;
      } else if (selected) {
        data = getDataToSend(selectedObj, processTrackId);
      } else {
        data = getDataToSend(selectedObj);
      }
      showSnackbar({ message: SnackbarMessage.ROLLOVER_PROGRESS });
      if (data.length) callService(INSER_TOPIC, { data });
      setNewTaxYear("");
    },
    [rolloverOptionsArr, selectedRollover],
  );

  const requestChecked = () => {
    requestRollover();
    setNewTaxYear("");
    setSelectedRollover(null);
    setScheduledDate(undefined);
    setSelectedDate(null);
    setNotificationSettings(DEFAULT_NOTIFICATION_SETTINGS);
  };

  const cancelRollover = useCallback((rolloverId: number) => {
    showSnackbar({ message: SnackbarMessage.CANCEL_SCHEDULE });
    callService(CANCEL_SCHEDULE, { siteId: parseInt(siteID), rolloverId });
  }, []);

  const callService = async (option: string, params: any) => {
    try {
      const { siteId, rolloverId, data } = params;
      const userName = user?.profile.preferred_username;

      if (option === SCHEDULE) {
        // Calculate firstNotificationDate: today's date if firstEmail is selected      

        const firstNotificationDate: string | undefined = notificationSettings.firstEmail
          ? formatDateForAPI(new Date())
          : undefined;

        // Second notification date: selectedDate minus secondEmailDays (date-only)
        let secondNotificationDate: string | undefined = undefined;

        if (
          notificationSettings.secondEmail &&
          notificationSettings.secondEmailDays != undefined &&
          params.selectedDate
        ) {
          const selectedDateObj = new Date(params.selectedDate);

          selectedDateObj.setDate(
            selectedDateObj.getDate() - notificationSettings.secondEmailDays
          );

          secondNotificationDate = formatDateForAPI(selectedDateObj);
        }

        // Scheduled date (date-only)
        const scheduledDate = formatDateForAPI(params.selectedDate);

        const cancelationEmail: string =
          notificationSettings.cancelationEmail.toString();

        await rollOverClientApi.taxPortal_ScheduleRollover(
          siteId,
          params.rollOverType,
          userName,
          scheduledDate,
          data,
          taxYear,
          newTaxYear,
          params.appName,
          firstNotificationDate,
          secondNotificationDate,
          cancelationEmail
        );
      }
      if (option === INSER_TOPIC) {
        await taxPortalClientApi.taxPortal_InsertTopic(data);
      }
if (option === ROLLOVER_NOW) {
          if(params.requestRollover.rollOverType === SITE_ROLLOVER_COMPLETION)
          {            
            const payload: RolloverRequestMessage = {
              hqSiteId: parseInt(siteID),
              taxYear: Number(params.requestRollover?.taxYear ?? taxYear),
              newTaxYear: Number(params.requestRollover?.newTaxYear ?? newTaxYear),
              rolloverStepName: "Update_SiteConfig",
              userName: userName,
              appName: params.requestRollover?.appName ?? appName ?? "TP",
              mdmClientId: params.requestRollover?.mdmClientId ?? mdmClientId,
              hqRolloversTracksId: params.requestRollover?.id,
              correlationId: params.requestRollover?.correlationId
            };
            await rollOverClientApi.archiveFile_InsertTopic(payload); 
            fetchData();       
          }
          else {
          const message: RolloverRequestMessage = {
            hqSiteId: parseInt(siteID),
            hqRolloversTracksId: params.requestRollover.id,
            userName,
            mdmClientId: params.requestRollover.mdmClientId,
            appName: params.requestRollover.appName || "TP",
            taxYear: Number(params.requestRollover.taxYear),
            newTaxYear: Number(params.requestRollover.newTaxYear),
            isFSTemplate: params.requestRollover.isFSTemplate,
            isheetName: params.requestRollover.rollOverType,
            rolloverStepName: params.requestRollover.rollOverType === "FILES" ? "Create_Archival_Folder" : "ISheet_Rollover",
            correlationId: params.requestRollover.correlationId
          };
        await createSitesClientApi.taxPortal_RolloverNow(message);
      }
    }
      
      if (option === CANCEL_SCHEDULE)
        await rollOverClientApi.taxPortal_CancelScheduledRollover(
          siteId,
          rolloverId,
          data,
          userName,
        );
      if (option !== INSER_TOPIC && option !== ROLLOVER_NOW) fetchData();
    } catch (err: any) {
      const { status, response } = err;
      if (status === 409) {
        showSnackbar({ message: response, severity: "error" });
      } else {
        showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
      }
    } finally {
      handleCloseModal();
    }
  };

  const getDataToSend = (
    rolloverSelected: SelectorType[],
    processTrackId?: number,
  ) => {
    return rolloverSelected.map(
      (item): ServiceMessage => ({
        eventType: (Number(item.id) === ServiceBusEnum.FILES_ROLLOVER) ? ServiceBusEnum.FILES_ROLLOVER : ServiceBusEnum.IR_ROLLOVER,//Number(id),
        mdmClientId,
        siteId: parseInt(siteID),
        appName: appName ?? "TP",
        userName: user?.profile.preferred_username,
        scheduleDate:(selectedDate ?? new Date()),
        year: Number(taxYear),
        newTaxYear: processTrackId ? Number(newTaxValue) : Number(newTaxYear),
        processTrackId,
        isheetType: item.name,
      }),
    );
  };


  // Compute whether to show the DateBox based on current date + days prior value > scheduled date
  const shouldShowDateBox = (() => {
    if (!scheduledDate) return false;
    // Hide the scheduled DateBox when second email is not selected
    if (!notificationSettings.secondEmail) return false;
    const daysPrior = notificationSettings.secondEmailDays ?? 1;
    const currentDatePlusDays = new Date();
    currentDatePlusDays.setDate(currentDatePlusDays.getDate() + daysPrior);
    currentDatePlusDays.setHours(0, 0, 0, 0);
    const scheduledDateOnly = new Date(scheduledDate);
    scheduledDateOnly.setHours(0, 0, 0, 0);
    return currentDatePlusDays > scheduledDateOnly;
  })();
  //End

  // Enable Notification Preferences only when all 4 top fields have value:
  // - New tax year is a 4-digit string and greater than current tax year
  // - A rollover process is selected
  // - Schedule Rollover date is selected when microservice is enabled
  // Additionally, disable when "Information Request" or "Project Tracking" is selected
  const isNotificationSectionEnabled =
    isValid &&
    selectedRollover &&
    isNewTaxYearValid &&
    selectedDate != null &&
    selection === "FILES";

  const onRolloverChanged = useCallback((e: any) => {
    setSelectedRollover(e.value);
    setNotificationSettings(DEFAULT_NOTIFICATION_SETTINGS);
  }, []);

  const onNewTaxYearChanged = useCallback(
    (e: any) => {
      setNewTaxYear(e.value);
      if (Number(e.value) > Number(taxYear)) {
        setNewTaxYearValid(true);
      } else {
        setNewTaxYearValid(false);
      }
    },
    [taxYear],
  );

  // Separate retry rollover function
  const retryRollover = useCallback(
    async (rollOverType: string, rolloverTrackId: number) => {
      try {
        showSnackbar({ message: SnackbarMessage.ROLLOVER_PROGRESS });

        const userName = user?.profile.preferred_username || "RSMTaxPortalAdmin";

        // Find the rollover data from the grid
        const rolloverData = data.rollovers?.find((r: any) => r.id === rolloverTrackId);

        // Prepare the retry request message with all available data
        const retryMessage: RolloverRequestMessage = {
          hqRolloversTracksId: rolloverData?.id,
          hqSiteId: parseInt(siteID),
          taxYear: Number(rolloverData?.taxYear),
          newTaxYear: Number(rolloverData?.newTaxYear),
          userName: userName,
          appName: appName,
          mdmClientId: mdmClientId,
          isheetName: rollOverType
        };

        const result = await rollOverClientApi.taxPortal_RetryRollover(retryMessage);

        if (result.isSuccess) {
          await fetchData();
          let mesg: any = result.message || SnackbarMessage.ROLLOVER_PROGRESS;
          showSnackbar({
            message: mesg,
            severity: "success"
          });
        } else {
          let mesg: any = result.message || SnackbarMessage.ERROR;
          showSnackbar({
            message: mesg,
            severity: "error"
          });
        }
      } catch (err: any) {
        console.error("Retry rollover error:", err);
        showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
      }
    },
    [data, siteID, taxYear, appName, mdmClientId, user, fetchData, showSnackbar]
  );

// Check if FILES rollover is completed
const isFilesRolloverComplete = data.rollovers?.some(
  (r: any) => r.taxYear === Number(taxYear) && r.rollOverType === "FILES" &&
    ["SUCCESS", "PARTIALLYSUCCESS"].includes(r.rollOverStatus)
);

// Check if Site Rollover Completion already exists
const isSiteRolloverCompletionExists = data.rollovers?.some(
  (r: any) => r.taxYear === Number(taxYear) && r.rollOverType === "SITEROLLOVERCOMPLETION" &&
    r.rollOverStatus === "NOTSTARTED"
);

const scheduleCompleteRollover = async () => {
  if (!isFilesRolloverComplete) {
    showSnackbar({ message: "Site file rollover is not completed yet.", severity: "error" });
    return;
  }

  const rollover = data.rollovers?.find(
    (r: any) => r.taxYear === Number(taxYear) && r.rollOverType === "SITEROLLOVERCOMPLETION"
  );

  if (!rollover) {
    showSnackbar({ message: "Site rollover completion record was not found.", severity: "error" });
    return;
  }

  try {
    showSnackbar({ message: "Completing rollover - scheduling..." });
      await rollOverClientApi.taxPortal_ScheduleSiteRolloverTrack(rollover.id);
      fetchData();

    showSnackbar({ message: "Complete rollover scheduled successfully.", severity: "success" });
  } catch (error) {
    showSnackbar({ message: "Failed to schedule complete rollover.", severity: "error" });
  }
};
  const onSettingsChange = (
    updater: NotificationSettings | ((prev: NotificationSettings) => NotificationSettings)
  ) => {
    setNotificationSettings(prev => {
      const next =
        typeof updater === "function"
          ? updater(prev)
          : updater;

      return isEqual(prev, next) ? prev : next;
    });
  };

  const firstEmail = !!selectedRolloverItem?.firstNotificationDate;

  const secondEmailDays = getDaysDifference(
    selectedRolloverItem?.secondNotificationScheduledDate,
    selectedRolloverItem?.scheduledDate
  );

  const secondEmail = selectedRolloverItem?.secondNotificationScheduledDate ? true : false;

  const cancelationEmail = !!selectedRolloverItem?.sendCancellationEmail;

  const notifyClient = firstEmail || secondEmail || cancelationEmail;

  const settings = {
    notifyClient,
    firstEmail,
    secondEmail,
    secondEmailDays: secondEmailDays ?? undefined,
    cancelationEmail,
  };

  return (
    <section className="hq-page">
      <div
        style={{ marginLeft: "20px", marginRight: "20px", marginTop: "20px" }}
      >
        <header>
          {/* <header className="hq-page__rollover-header"> */}
          <div className="rollover-header-layout">
            {/* Left: Rollover Details */}
            <div className="rollover-details-section">
              <h3 className="section-title">Rollover Details</h3>
              <div className="form-grid">
                <div className="form-field">
                  <label className="field-label">Current Tax Year</label>
                  <TextBox
                    stylingMode="outlined"
                    inputAttr={taxYear}
                    value={taxYear ? String(taxYear) : ""}
                    disabled={true}
                    width="320px"
                  />
                </div>
                <div className="form-field">
                  <label className="field-label">New Tax Year</label>
                  <TextBox
                    id="newTaxYear"
                    stylingMode="outlined"
                    value={newTaxYear}
                    onValueChanged={onNewTaxYearChanged}
                    valueChangeEvent="input"
                    width="320px"
                    placeholder="Enter rollover tax year"
                  />
                </div>
                <div className="form-field">
                  <label className="field-label">Rollover Process</label>
                  <SelectBox
                    styling-mode="outlined"
                    dataSource={rolloverOptionsDropdown}
                    displayExpr="name"
                    valueExpr="id"
                    placeholder="Select rollover process"
                    value={selectedRollover}
                    onValueChanged={onRolloverChanged}
                    width="320px"
                  /> 
                  <Button
                    onClick={handleOpenModal}
                    variant="contained"
                    className="submit-rollover-btn"
                    disabled={
                      !(
                        isValid &&
                        selectedRollover &&
                        isNewTaxYearValid &&
                        selectedDate !== null
                      ) || !isNotificationValid
                    }
                    style={{
                      marginTop: "10px",
                      width: "fit-content",
                      textTransform: "none",
                      justifyContent: "flex-start"
                    }}
                  >
                    Submit Rollover Request
                  </Button>
                </div>
                <div className="form-field">
                  <label className="field-label">Schedule Rollover</label>
                  {(
                    <RolloverDateBox
                      {...(selectedRollover
                        ? { disabled: false }
                        : { disabled: true })}
                      rolloverType={selectedRollover ? selectedRollover : 0}
                      appName={appName ?? "TP"}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Right: Notification Preferences + Submit */}
            <div className="notification-section">
              {selection === "FILES" ? (
                <>
                  <div className="notification-header-row">
                    <h3 className="section-title">Notification Preferences</h3>
                    {/* <IconButton size="small" className="settings-icon">
                    <SettingsIcon fontSize="small" />
                  </IconButton> */}
                  </div>
                  <NotificationPreferences
                    settings={notificationSettings}
                    scheduledDate={selectedDate ?? undefined}
                    onSettingsChange={onSettingsChange}
                    onValidationChange={(valid) => setIsNotificationValid(valid)}
                  />
                </>
              ) : (
                // Placeholder to preserve spacing when hidden
                <div style={{ minHeight: 110, marginBottom: 24 }} />
              )}
            </div>
          </div>
          <div className="submit-button-wrapper">
            <Button
              onClick={() => setIsDisplayCompleteRolloverModal(true)}
              variant="contained"
              className="complete-rollover-btn"
              disabled={!isFilesRolloverComplete || !isSiteRolloverCompletionExists}
            >
              Complete Rollover
            </Button>
             <Tooltip
                  title="Use this option when certain rollover processes are not required. Completing rollover will apply the required tax-year updates. Rollover Files must be completed before this option can be used."
                  arrow
                  placement="top"
                >
                  <IconButton
                    size="small"
                    sx={{
                      width: 32,
                      height: 32,
                      padding: 0.5,
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 22, color: "#009CDE" }} />
                  </IconButton>
                </Tooltip>
          </div>
        </header>

        <NewGridRollover
          data={data}
          appName={appName}
          handleOpenModal={handleOpenRolloverFilesModal}
          handleOpenStatusDetailsModal={handleOpenStatusDetailsModal}
          refreshFunction={fetchData}
          requestRollover={requestRollover}
          retryRollover={retryRollover}
          cancelRollover={cancelRollover}
          requestRolloverNow={requestRolloverNow}
        />
      </div>

      <DialogBox
        maxWidth={"xs"}
        title={ROLLOVER_SUBMIT_MODAL.TITLE}
        btnOpenText={ROLLOVER_SUBMIT_MODAL.CONTINUE_BUTTON}
        btnCloseText={ROLLOVER_SUBMIT_MODAL.CANCEL_BUTTON}
        isDisplayModal={isDisplayModal}
        btnCloseTop={false}
        handleContinue={requestChecked}
        handleClose={handleCloseModal}
        dialogContentSx={{ borderTop: "1px solid #e0e0e0" }}
        dialogTitleSx={{ color: '#515356', display: "flex", alignItems: "center", gap: "8px", }}
        icon={<InfoOutlinedIcon sx={{ color: "#009CDE", width: 22, height: 22 }} />}
      >
        <div className="dialog-body">
          <p className="dialog-text">
            {getRolloverSubmitMessage(
              selection || "",
              scheduleDays,
              selectedDate ? new Date(selectedDate).toLocaleDateString() : undefined
            ).message}
          </p>
          <p className="dialog-text">
            {ROLLOVER_SUBMIT_MODAL.CLOSING_MESSAGE}
          </p>
        </div>
      </DialogBox>

      <DialogBox
        maxWidth={"md"}
        title={'Notification preferences'}
        btnOpenText={'Cancel'}
        isDisplayModal={isDisplayRolloverFilesModal}
        handleContinue={handleCloseRolloverFilesModal}
        btnCloseText={''}
        dialogContentSx={{ borderTop: "1px solid #e0e0e0" }}
        handleClose={handleCloseRolloverFilesModal}
        dialogTitleSx={{ color: '#515356', display: "flex", alignItems: "center", gap: "8px", }}
      >
        <NotificationPreferences
          settings={{
            notifyClient: settings.notifyClient,
            firstEmail: settings.firstEmail,
            secondEmail: settings.secondEmail,
            secondEmailDays: settings.secondEmailDays,
            cancelationEmail: settings.cancelationEmail,
          }}
          disabled={true}
          scheduledDate={selectedRolloverItem?.scheduledDate ?? undefined}
          isReadOnly={true}
          selectedRolloverItem={selectedRolloverItem}
        />
      </DialogBox>
      <DialogBox
        maxWidth={"xs"}
        title="Complete Rollover"
        btnOpenText="Continue"
        btnCloseText="Cancel"
        isDisplayModal={isDisplayCompleteRolloverModal}
        btnCloseTop={false}
        handleContinue={() => {
          setIsDisplayCompleteRolloverModal(false);
          scheduleCompleteRollover();
        }}
        handleClose={() => setIsDisplayCompleteRolloverModal(false)}
        dialogContentSx={{ borderTop: "1px solid #e0e0e0" }}
        dialogTitleSx={{ color: '#515356', display: "flex", alignItems: "center", gap: "8px" }}
        icon={<InfoOutlinedIcon sx={{ color: "#009CDE", width: 22, height: 22 }} />}
      >
        <div className="dialog-body">
          <p className="dialog-text">
            Are you sure you want to schedule SITE ROLLOVER COMPLETION? This will update the site configuration and apply tax-year updates.
          </p>
        </div>
      </DialogBox>
      <ModalRolloverStatusDetails
        rolloverId={selectedDetailsId!}
        handleClose={handleCloseStatusDetailsModal}
        isDisplayModal={isDisplayStatusDetailsModal}
      />
    </section>
  );
}